import { ApiService } from '@/api';
import {
  GeekItem,
  GeekItemWithRole,
  DateRange,
  SearchFilter, PhraseSearchFilter,
} from '@/models';
import { OpportunityNewsItem } from '@/models/dashboard';
import { OpportunitiesCount } from '@/models/geek-profile';
import {
  GeekCounter,
  OpportunityListItem,
  Opportunity,
  Tag,
  NewOpportunity,
  Role,
  Counter,
  OpportunityMembershipStatus,
  OpportunityFilters,
  OpportunityDraft,
  NewOpportunityDraft,
} from '@/models/opportunities';
import { FinalizationFormData, CancelationFormData } from '@/models/opportunities/form-data';
import { OpportunityDraftListItem } from '@/models/opportunities/opportunity-draft-list-item.model';

export class OpportunitiesApi {
  private readonly opportunitiesBaseUrl = '/api/opportunities';
  private readonly geekBaseUrl = '/api/geeks';

  public async fetchOpportunities(
    filters?: OpportunityFilters,
    offset: number = 0,
    limit: number = 20): Promise<{ data: OpportunityListItem[], totalCount: number }> {
    const response = await ApiService.post(
      `${this.opportunitiesBaseUrl}/search`,
      filters || {}, {params: {offset, limit}},
      );

    const data: OpportunityListItem[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchNews(
    offset: number = 0,
    limit: number = 6,
  ): Promise<{ data: OpportunityNewsItem[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.opportunitiesBaseUrl}/newest`,
      {params: {offset, limit}},
    );
    const data: OpportunityNewsItem[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchOpportunity(id: string): Promise<Opportunity> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/${id}`);
    return response.json();
  }

  public async changeCandidateStatus(
    opportunityId: string,
    geekId: string,
    status: OpportunityMembershipStatus,
    reason: string,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/members/${geekId}`,
      { status, reason },
    );
  }

  public async approveOpportunity(opportunityId: string): Promise<void> {
    await ApiService.put(`${this.opportunitiesBaseUrl}/${opportunityId}/status/recruiting`);
  }

  public async sendMessage(opportunityId: string, message: string): Promise<void> {
    await ApiService.post(
      `${this.opportunitiesBaseUrl}/${opportunityId}/messages`,
      message,
    );
  }

  public async giveFeedback(
    opportunityId: string,
    message: string,
  ): Promise<void> {
    await ApiService.post(
      `${this.opportunitiesBaseUrl}/${opportunityId}/feedback`,
      message,
    );
  }

  public async rejectOpportunity(
    opportunityId: string,
    reason: string,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/status/rejected`,
      { reason },
    );
  }

  public async cancelRecruitingOpportunity(
    opportunityId: string,
    reason: string,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/status/cancel-recruiting`,
      { reason },
    );
  }

  public async cancelOngoingOpportunity(
    opportunityId: string,
    formData: CancelationFormData,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/status/cancel-ongoing`,
      formData,
    );
  }

  public async launchOpportunity(opportunityId: string): Promise<void> {
    await ApiService.put(`${this.opportunitiesBaseUrl}/${opportunityId}/status/ongoing`);
  }

  public async deleteOpportunity(opportunityId: string, reason: string): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/status/deleted`,
      { reason },
    );
  }

  public async finalizeOpportunity(
    opportunityId: string,
    formData: FinalizationFormData,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/status/finalized`,
      formData,
    );
  }

  public async fetchFinalizationDraft(opportunityId: string): Promise<FinalizationFormData> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/${opportunityId}/finalization-draft`);

    return response.json();
  }

  public async keepFinalizationDraft(
    opportunityId: string,
    formData: FinalizationFormData,
  ): Promise<void> {
    await ApiService.post(
      `${this.opportunitiesBaseUrl}/${opportunityId}/finalization-draft`,
      formData,
    );
  }

  public async editFinalizationDraft(
    opportunityId: string,
    formData: FinalizationFormData,
  ): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${opportunityId}/finalization-draft`,
      formData,
    );
  }

  public async changeMembershipStatus(id: string, data: {status: OpportunityMembershipStatus} & any): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${id}/members/self`,
      data,
    );
  }

  public async fetchDepartments(): Promise<string[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/departments`);

    return response.json();
  }

  public async fetchLevels(): Promise<string[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/levels`);

    return response.json();
  }

  public async fetchTags(): Promise<Tag[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/tags`);

    return response.json();
  }

  public async fetchPopularTags(): Promise<Tag[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/tags/most-popular`);

    return response.json();
  }

  public async createOpportunity(newOpportunity: NewOpportunity): Promise<Opportunity> {
    const response = await ApiService.post(
      `${this.opportunitiesBaseUrl}`,
      newOpportunity,
    );

    return response.json();
  }

  public async editOpportunity({id, ...opportunity}: NewOpportunity & {id: string}): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/${id}`,
      opportunity,
    );
  }

  public async fetchGeeksWithRoles(
    filters: PhraseSearchFilter,
    offset: number = 0,
    limit: number = 50,
  ): Promise<{ data: GeekItemWithRole, totalCount: number }> {
    const response = await ApiService.get(
      `${this.opportunitiesBaseUrl}/roles/geeks`,
      {params: {offset, limit, searchPhrase: filters?.searchPhrase}},
    );
    const data: GeekItemWithRole = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchCandidates(searchPhrase: string): Promise<GeekItem[]> {
    const response = await ApiService.get(
      `${this.opportunitiesBaseUrl}/roles/candidates`,
      { params: { searchPhrase } });
    const data: GeekItem[] = await response.json();

    return data;
  }

  public async addMember(opportunityId: string, memberId: string): Promise<void> {
    const response = await ApiService.post(
      `${this.opportunitiesBaseUrl}/${opportunityId}/members`,
      { GeeksIds: [memberId] });

    return response.json();
  }

  public async changeGeekRole(geekWithRole: GeekItemWithRole, role: Role): Promise<void> {
    await ApiService.put(
      `${this.opportunitiesBaseUrl}/roles/geeks/${geekWithRole.geek.id}`,
      { role },
    );
  }

  public async fetchOpportunitiesCounters(): Promise<Counter[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/counters`);

    return response.json();
  }

  public async fetchCreators(): Promise<GeekItem[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/creators`);

    return response.json();
  }

  public async fetchIssuers(): Promise<GeekItem[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/roles/issuers`);

    return response.json();
  }

  public async fetchApprovers(): Promise<GeekItem[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/roles/approvers`);

    return response.json();
  }

  public async fetchAdmins(): Promise<GeekItem[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/roles/admins`);

    return response.json();
  }

  public async fetchGeeksCounters(): Promise<GeekCounter[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/roles/geeks/counters`);

    return response.json();
  }

  public async fetchGeekOpportunitiesCount(geekId: string): Promise<OpportunitiesCount> {
    const response = await ApiService.get(`${this.geekBaseUrl}/${geekId}/opportunities-count`);

    return response.json();
  }

  public async fetchReport(dateRange: DateRange): Promise<Blob> {
    const response = await ApiService.get(
      `${this.opportunitiesBaseUrl}/statistics-report`,
      {
        params: {
          changedAfter: dateRange.from,
          changedBefore: dateRange.to,
        },
      },
    );

    return response.blob();
  }

  public async createOpportunityDraft(item: NewOpportunityDraft): Promise<OpportunityDraft> {
    const response = await ApiService.post(`${this.opportunitiesBaseUrl}/drafts`, item);
    return response.json();
  }

  public async deleteOpportunityDraft(id: string): Promise<void> {
    await ApiService.delete(`${this.opportunitiesBaseUrl}/drafts/${id}`);
  }

  public async editOpportunityDraft({id, ...item}: OpportunityDraft & {id: string}): Promise<void> {
    await ApiService.put(`${this.opportunitiesBaseUrl}/drafts/${id}`, item);
  }

  public async fetchOpportunityDraft(id: string): Promise<OpportunityDraft> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/drafts/${id}`);
    return response.json();
  }

  public async fetchOpportunitiesDrafts(): Promise<OpportunityDraftListItem[]> {
    const response = await ApiService.get(`${this.opportunitiesBaseUrl}/drafts`);
    return response.json();
  }
}

